import {
  type ConceptChunk,
  GetConceptChunksRequestConfig,
  type SeedWithDistances,
  useStorytellClient,
} from "@storytell/client";
import { dispatchGlobalEvent } from "@storytell/events";
import { SolidStreamingMarkdown, createSolidStreamingMarkdown } from "@storytell/markdown";
import type { Asset, AssetId, Concept, ConceptId, ViewId } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import { useQuery } from "@tanstack/solid-query";
import {
  TbAlertTriangle,
  TbArrowLeft,
  TbFilter,
  TbFilterOff,
  TbInfoCircle,
  TbLoader2,
  TbMessage2,
  TbX,
} from "solid-icons/tb";
import { For, Match, Show, Suspense, Switch, createMemo, createSignal } from "solid-js";
import { Dynamic } from "solid-js/web";
import { useWire } from "~/wire";
import type { FilterBuilder } from "../filtering/filterBuilder";
import { getAssetIcon } from "../utils/getAssetIcon";
import { MockSubscribeToConcept } from "./MockSubscribeToConcept";

export const NavigationConceptsPaneDetails = (props: {
  id: ConceptId | "";
  variant: "pane" | "chat";
  filterBuilder: FilterBuilder<Asset | Concept> | undefined;
  onConceptClick: (id: ConceptId | "") => void;
}) => {
  const wire = useWire();

  const memoId = createMemo(() => props.id);

  const query = useQuery(() => ({
    queryKey: ["concept-chunks", memoId()],
    enabled: !!memoId() && wire.auth.isReady(),
    queryFn: async () => {
      const client = useStorytellClient();
      const request = GetConceptChunksRequestConfig.withBearerToken(wire.auth.token());
      const response = await client.fetch(request, { conceptId: props.id as ConceptId }, undefined);
      if (response.isOk()) {
        return response.value;
      }
      console.error(response.error);
      throw response.error;
    },
    retry: 1,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 5,
  }));

  const concept = () =>
    props.id ? wire.concepts.get(props.id) || wire.concepts.oldValuesCache.get(props.id) : undefined;

  const assets = createMemo(() =>
    props.filterBuilder ? wire.assets.all().filter(props.filterBuilder.buildFilterFunction()) : wire.assets.all(),
  );

  const assetsTimelineFromSeeds = createMemo(() => {
    const timeline: { seed: SeedWithDistances; chunks: ConceptChunk[]; assetId: AssetId }[] = [];

    if (query.isLoading || query.isError) return timeline;

    for (const seed of concept()?.seeds ?? []) {
      if (!assets().find((a) => a.id === seed.assetId)) continue;

      const chunkSeed = query.data?.seeds[seed.id];
      const chunks = Object.values(chunkSeed?.chunks ?? {});

      timeline.push({
        seed,
        chunks: [...(chunks || [])],
        assetId: seed.assetId as AssetId,
      });
    }

    return timeline;
  });

  const stats = createMemo(() => {
    if (query.isLoading || query.isError)
      return {
        filteredAssets: 0,
        unfilteredAssets: 0,
        filteredChunks: 0,
        unfilteredChunks: 0,
        filteredOutAssets: 0,
        filteredOutChunks: 0,
      };

    const unfilteredAssets = new Set(concept()?.seeds?.map((s) => s.assetId));
    const filteredAssets = new Set(
      assets()
        .filter((a) => unfilteredAssets.has(a.id))
        .map((a) => a.id),
    );

    const filteredChunks = new Set(assetsTimelineFromSeeds().flatMap((a) => a.chunks.map((c) => c.id)));
    const unfilteredChunks = new Set(
      concept()?.seeds?.flatMap((s) => Object.values(query.data?.seeds[s.id]?.chunks ?? {}).map((c) => c.id)),
    );

    const filteredOutAssets = unfilteredAssets.size - filteredAssets.size;
    const filteredOutChunks = unfilteredChunks.size - filteredChunks.size;

    return {
      filteredAssets: filteredAssets.size,
      unfilteredAssets: unfilteredAssets.size,
      filteredChunks: filteredChunks.size,
      unfilteredChunks: unfilteredChunks.size,
      filteredOutAssets,
      filteredOutChunks,
    };
  });

  const relatedConcepts = () =>
    Object.entries(concept()?.distances ?? {})
      .filter((c) => c[1] <= 0.75)
      .map((c) => ({
        ...(wire.concepts.get(c[0] as ConceptId) || wire.concepts.oldValuesCache.get(c[0] as ConceptId)),
        distance: c[1],
      }));

  const onAssetClick = (assetId: string) => {
    dispatchGlobalEvent("open-asset-in-pane", { assetId });
  };
  let ref!: HTMLDivElement;

  const [filterExpanded, setFilterExpanded] = createSignal(false);
  return (
    <div ref={ref} class="px-2">
      <Suspense>
        <Show when={concept()}>
          {(concept) => (
            <div>
              <div
                class="grid gap-4"
                classList={{
                  "grid-cols-[1fr]": props.variant === "chat",
                  "grid-cols-[1fr_max-content]": props.variant === "pane",
                }}
              >
                <div class="flex flex-col gap-2">
                  <div class="flex items-center gap-2">
                    <StButton
                      iconClass="size-5"
                      onClick={() => {
                        props.onConceptClick("");
                      }}
                      noColor
                      icon={TbArrowLeft}
                      class="py-0.5 dark:border-primary-border shrink-0 bg-brand hover:bg-brand-hover text-on-brand"
                    >
                      {wire.knowledgeUi.activeView() === "all"
                        ? "All Knowledge"
                        : wire.views.get(wire.knowledgeUi.activeView() as ViewId)?.name}
                    </StButton>
                    <Show when={(props.filterBuilder?.state.filter.groups[0]?.filters.length || 0) > 0}>
                      <StButton
                        icon={filterExpanded() ? TbFilterOff : TbFilter}
                        class="dark:border-primary-border p-1"
                        onClick={() => setFilterExpanded((s) => !s)}
                      >
                        {filterExpanded()
                          ? "Hide filters"
                          : props.filterBuilder!.state.filter.groups[0]?.filters.length > 0
                            ? `${props.filterBuilder!.state.filter.groups.length} filter${props.filterBuilder!.state.filter.groups.length === 1 ? "" : "s"}`
                            : "Filter"}
                      </StButton>
                    </Show>
                  </div>

                  <div classList={{ "mt-0": props.variant === "chat", "mt-4 px-3": props.variant === "pane" }}>
                    <h2
                      class="font-semibold mb-2"
                      classList={{ "text-lg": props.variant === "chat", "text-xl": props.variant === "pane" }}
                    >
                      @{concept().name}
                    </h2>
                    <p
                      classList={{ "text-sm": props.variant === "chat", "text-base": props.variant === "pane" }}
                      class="text-secondary-text"
                    >
                      {concept().summary}
                    </p>
                  </div>
                </div>

                <Show when={props.variant === "pane"}>
                  <div class="shrink-0 pt-3 flex flex-col gap-3" classList={{ "px-3": props.variant === "pane" }}>
                    <StButton
                      noColor
                      class="bg-brand hover:bg-brand-hover text-on-brand shrink-0 text-sm"
                      onClick={() => {
                        dispatchGlobalEvent("add-items-to-prompt-scope", { ids: [concept().id] });
                      }}
                      icon={TbMessage2}
                    >
                      Chat with this Concept
                    </StButton>

                    <MockSubscribeToConcept mount={ref} conceptName={concept().name} />
                  </div>
                </Show>
              </div>
              <div
                class="mt-4"
                classList={{
                  "grid gap-4 grid-cols-[2fr_1fr] px-3": props.variant === "pane",
                }}
              >
                <div class="rounded-xl border border-primary-border p-4 bg-panel-hover max-w-full">
                  <Switch>
                    <Match when={query.isLoading}>
                      <div class="flex items-center justify-center gap-2">
                        Loading concept timeline
                        <TbLoader2 class="animate-spin size-4 text-muted-text" />
                      </div>
                    </Match>

                    <Match when={query.isError}>
                      <div class="flex items-center justify-center gap-2">
                        Error loading concept timeline
                        <TbAlertTriangle class="size-4 text-muted-text" />
                      </div>
                    </Match>

                    <Match when={stats().filteredChunks > 0 && stats().filteredAssets > 0}>
                      <div class="flex items-center justify-between mb-2">
                        <h4
                          class="font-medium"
                          classList={{
                            "text-base": props.variant === "chat",
                            "text-lg": props.variant === "pane",
                          }}
                        >
                          Timeline
                        </h4>
                        <a
                          href="https://go.storytell.ai/learn-timeline"
                          target="_blank"
                          rel="noreferrer"
                          class="text-xs text-brand hover:underline flex items-center gap-1"
                        >
                          <TbInfoCircle class="size-3" />
                          Learn about Concept Timelines
                        </a>
                      </div>
                      <p
                        class="text-secondary-text"
                        classList={{
                          "text-sm mb-3": props.variant === "chat",
                          "text-base mb-6": props.variant === "pane",
                        }}
                      >
                        This concept is built from {stats().filteredChunks} citations in{" "}
                        {stats().filteredAssets === 1 ? "1 asset." : `these ${stats().filteredAssets} assets.`}
                      </p>

                      <For each={assetsTimelineFromSeeds()}>
                        {(a, index) => {
                          const [showAllChunks, setShowAllChunks] = createSignal(false);

                          return (
                            <Show when={wire.assets.get(a.assetId)}>
                              {(asset) => (
                                <div class="flex items-start gap-2 relative mb-2">
                                  <Show when={index() !== assetsTimelineFromSeeds().length - 1}>
                                    <div class="absolute top-8 left-[0.9125rem] -bottom-4 w-0.5 bg-primary-border" />
                                  </Show>
                                  <div class="w-8 h-8 shrink-0 bg-primary-border rounded-lg flex items-center justify-center">
                                    <Dynamic component={getAssetIcon(asset())} class="w-4 h-4 text-primary-text" />
                                  </div>
                                  <div
                                    classList={{
                                      "-mt-0.5": props.variant === "chat",
                                    }}
                                  >
                                    <p
                                      class="font-medium"
                                      classList={{
                                        "text-sm": props.variant === "chat",
                                        "text-lg": props.variant === "pane",
                                      }}
                                    >
                                      {a.seed.name}
                                      {/* <span class="text-secondary-text text-xs font-normal">
                                      {" "}
                                      - {a.chunks[0]?.modifiedAt ? dayjs(a.chunks[0]?.modifiedAt).format("LLL") : ""}
                                    </span> */}
                                    </p>
                                    <p
                                      class="mb-2"
                                      classList={{
                                        "text-xs": props.variant === "chat",
                                        "text-sm": props.variant === "pane",
                                      }}
                                    >
                                      from{" "}
                                      <button
                                        type="button"
                                        class="font-medium text-brand cursor-pointer hover:underline"
                                        onClick={() => onAssetClick(a.assetId)}
                                      >
                                        {asset().filename}
                                      </button>
                                    </p>
                                    <div class="space-y-2 mb-3">
                                      <For each={showAllChunks() ? a.chunks : a.chunks.slice(0, 1)}>
                                        {(c) => {
                                          const [clamped, setClamped] = createSignal(true);
                                          return (
                                            // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
                                            <div
                                              onClick={() => setClamped(!clamped())}
                                              class="cursor-pointer w-full p-3 px-4 hover:bg-tile-hover hover:border-brand hover:shadow-md transition-all duration-200 rounded-xl border-2 border-muted-border dark:border-primary-border group relative"
                                              classList={{ "[&>.md-document]:line-clamp-6": clamped() }}
                                            >
                                              <SolidStreamingMarkdown
                                                doc={
                                                  createSolidStreamingMarkdown(
                                                    c.text.replaceAll(/\[(source:.*|\d+)\]/g, ""), //.replaceAll("\n", "\n\n")
                                                  ).doc
                                                }
                                                options={{
                                                  classes: {
                                                    document: `wrap-break-word whitespace-break-spaces ${props.variant === "pane" ? "text-sm" : "text-xs"}  [&_*:last-child]:mb-0`,
                                                    heading: (node) => {
                                                      switch (node.level) {
                                                        case 1:
                                                          return props.variant === "pane"
                                                            ? "font-medium text-base mb-1"
                                                            : "font-medium text-sm mb-1";
                                                        case 2:
                                                          return props.variant === "pane"
                                                            ? "font-medium text-base mb-1"
                                                            : "font-medium text-sm mb-1";
                                                        case 3:
                                                          return props.variant === "pane"
                                                            ? "font-medium text-base mb-1"
                                                            : "font-medium text-sm mb-1";
                                                        case 4:
                                                          return props.variant === "pane"
                                                            ? "font-medium text-base mb-1"
                                                            : "font-medium text-sm mb-1";
                                                        case 5:
                                                          return props.variant === "pane"
                                                            ? "font-medium text-sm mb-1"
                                                            : "font-medium text-xs mb-1";
                                                        case 6:
                                                          return props.variant === "pane"
                                                            ? "font-medium text-sm mb-1"
                                                            : "font-medium text-xs mb-1";
                                                        default:
                                                          return "";
                                                      }
                                                    },
                                                    // paragraph: "mb-2",
                                                    list: (node) => {
                                                      switch (node.listData?.type) {
                                                        case "ordered":
                                                          return "list-decimal pl-4";
                                                        case "bullet":
                                                          return "list-disc pl-4";
                                                        default:
                                                          return "";
                                                      }
                                                    },
                                                    paragraph: "mb-4",
                                                    // text: "animate-fade-in",
                                                    item: "mb-2",
                                                    code: "bg-black/10 dark:bg-white/10 px-0.5 py-0.5 rounded text-sm",
                                                    blockQuote:
                                                      "border-l-2 border-indigo-100 dark:border-indigo-700 pl-2 py-1 my-1",
                                                    tableWrapper:
                                                      "overflow-x-auto w-full max-w-full rounded-lg border border-muted-border",
                                                    table: "",
                                                    tableRow: "border-b border-muted-border [&:last-child]:border-b-0",
                                                    tableCell:
                                                      "px-2 py-2 border-r border-muted-border [&:last-child]:border-r-0",
                                                    tableHead:
                                                      "bg-panel text-sm font-semibold [&_td]:whitespace-nowrap",
                                                    tableBody: "bg-tile",
                                                    link: "text-brand underline break-all",
                                                  },
                                                }}
                                              />
                                              <Show when={clamped()}>
                                                <div class="absolute bottom-2 left-2 right-2 h-12 bg-gradient-to-t from-tile to-transparent pointer-events-none rounded-b-xl" />
                                              </Show>
                                            </div>
                                          );
                                        }}
                                      </For>
                                    </div>
                                    <Show when={a.chunks.length > 1}>
                                      <button
                                        onClick={() => setShowAllChunks(!showAllChunks())}
                                        type="button"
                                        class="text-xs text-brand cursor-pointer hover:underline"
                                      >
                                        {showAllChunks()
                                          ? " Show less"
                                          : ` + ${a.chunks.length - 1} more source${a.chunks.length - 1 === 1 ? "" : "s"}`}
                                      </button>
                                    </Show>
                                  </div>
                                </div>
                              )}
                            </Show>
                          );
                        }}
                      </For>

                      <Show when={stats().filteredOutChunks > 0 && stats().filteredOutAssets > 0}>
                        <p class="text-base text-secondary-text mt-6 mb-3">
                          This concept is also built from {stats().filteredOutChunks} additional citations in{" "}
                          {stats().filteredOutAssets} assets that are not shown because they fall outside your{" "}
                          {wire.views.get(wire.knowledgeUi.activeView() as ViewId)?.name} data view.
                        </p>

                        <StButton
                          icon={TbFilterOff}
                          onClick={() => {
                            props.filterBuilder?.resetFilter();
                          }}
                          class="dark:border-primary-border bg-tile hover:bg-tile-hover"
                          noColor
                        >
                          Remove filters
                        </StButton>
                      </Show>
                    </Match>

                    <Match when={true}>
                      <p class="text-base text-secondary-text mt-6 mb-3">
                        This concept is built from {stats().filteredOutChunks} citations in {stats().filteredOutAssets}{" "}
                        assets that are not shown because they fall outside your{" "}
                        {wire.views.get(wire.knowledgeUi.activeView() as ViewId)?.name} data view.
                      </p>

                      <StButton
                        icon={TbFilterOff}
                        onClick={() => {
                          props.filterBuilder?.resetFilter();
                        }}
                        class="dark:border-primary-border bg-tile hover:bg-tile-hover"
                        noColor
                      >
                        Remove filters
                      </StButton>
                    </Match>
                  </Switch>
                </div>

                <Show when={props.variant === "pane"}>
                  <div>
                    <div class="rounded-xl border border-primary-border p-4">
                      <h4 class="font-medium text-lg mb-2">Related Concepts</h4>

                      <div class="space-y-2">
                        <For each={[...relatedConcepts()].sort((a, b) => a.distance - b.distance)}>
                          {(c) => {
                            const percentage = 100 - c.distance * 100;
                            const getBarColor = (pct: number) => {
                              if (pct >= 70) return "bg-green-300 dark:bg-green-600";
                              if (pct >= 40) return "bg-yellow-300 dark:bg-yellow-600";
                              return "bg-red-300 dark:bg-red-600";
                            };

                            return (
                              <div>
                                <button
                                  type="button"
                                  class="text-primary-text text-xs w-full max-w-full flex items-center gap-2 bg-tile hover:bg-tile-hover rounded-lg p-2 group"
                                  onClick={() => props.onConceptClick(c?.id ?? "")}
                                >
                                  <span class="truncate whitespace-normal flex-auto text-left max-w-full group-hover:text-indigo-600 dark:group-hover:text-indigo-400 group-hover:font-bold transition-colors">
                                    {c?.name}
                                  </span>
                                  <div class="shrink-0 w-20 relative rounded-full border border-primary-border bg-canvas h-4">
                                    <div
                                      class={`absolute top-0 left-0 w-full h-full rounded-full ${getBarColor(percentage)}`}
                                      style={{ width: `${percentage}%` }}
                                    />
                                    <span class="absolute top-0 left-0 w-full h-full flex items-center justify-start text-xs text-black pl-1">
                                      {percentage.toFixed(0)}%
                                    </span>
                                  </div>
                                </button>
                              </div>
                            );
                          }}
                        </For>
                      </div>
                    </div>
                  </div>
                </Show>
              </div>
            </div>
          )}
        </Show>
      </Suspense>
    </div>
  );
};
